import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantsApi } from '../services/tenants';
import { QUERY_KEYS } from './queryKeys';
import { getApiErrorMessage } from '../utils/apiError';
import toast from 'react-hot-toast';
import type { KycStatus, SubmitGuarantorRequest } from '../types/kyc';

// ---------------------------------------------------------------------------
// Tenant self-service hooks
// ---------------------------------------------------------------------------

export function useOwnKycProfile() {
  return useQuery({
    queryKey: QUERY_KEYS.tenants.profile(),
    queryFn: tenantsApi.getOwnProfile,
    retry: (count, err: unknown) => {
      // Don't retry 404 (profile doesn't exist yet)
      const status = (err as { response?: { status?: number } })?.response?.status;
      return status !== 404 && count < 2;
    },
  });
}

export function useCreateKycProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tenantsApi.createProfile,
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEYS.tenants.profile(), data);
      toast.success('KYC profile created');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to create KYC profile')),
  });
}

export function useSubmitIdDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, docType }: { file: File; docType: string }) =>
      tenantsApi.submitIdDocument(file, docType),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEYS.tenants.profile(), data);
      toast.success('ID document submitted');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to submit ID document')),
  });
}

export function useSubmitGuarantor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitGuarantorRequest) => tenantsApi.submitGuarantor(data),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEYS.tenants.profile(), data);
      toast.success('Guarantor information submitted');
    },
    onError: (err: unknown) =>
      toast.error(getApiErrorMessage(err, 'Failed to submit guarantor info')),
  });
}

export function useRecordConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tenantsApi.recordConsent,
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEYS.tenants.profile(), data);
      toast.success('Consent recorded');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to record consent')),
  });
}

export function useSubmitForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tenantsApi.submitForReview,
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEYS.tenants.profile(), data);
      toast.success('Submitted for review, a manager will be in touch shortly');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to submit for review')),
  });
}

// ---------------------------------------------------------------------------
// Manager / Admin hooks
// ---------------------------------------------------------------------------

export function useKycProfiles(status?: KycStatus) {
  return useQuery({
    queryKey: QUERY_KEYS.tenants.list(status),
    queryFn: () => tenantsApi.listProfiles(status),
  });
}

export function useKycProfile(id: number | null) {
  return useQuery({
    queryKey: QUERY_KEYS.tenants.detail(id ?? 0),
    queryFn: () => tenantsApi.getProfile(id!),
    enabled: !!id,
  });
}

export function useApproveKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tenantsApi.approveKyc(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenants.all });
      toast.success('KYC approved, tenant role granted');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to approve KYC')),
  });
}

export function useRejectKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      tenantsApi.rejectKyc(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenants.all });
      toast.success('KYC rejected, tenant has been notified');
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to reject KYC')),
  });
}

export function useMyAssignment() {
  return useQuery({
    queryKey: ['tenants', 'my-assignment'],
    queryFn: tenantsApi.getMyAssignment,
    retry: false,
  });
}
